import { PublicClientApplication, type Configuration } from '@azure/msal-browser'

/**
 * MSAL configuration, built on first use rather than at module load.
 *
 * It used to construct `PublicClientApplication` eagerly. That made importing
 * anything in this directory enough to require a real Entra configuration —
 * so a local dev bypass could not exist, because the crash happened before
 * any provider had a chance to decide not to use Entra.
 */

function buildConfig(): Configuration {
  return {
    auth: {
      clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID}`,
      // window.location.origin for portability across environments (local,
      // SWA preview, production). The Entra app registration must list this
      // origin as a redirect URI — a trailing-slash mismatch is AADSTS50011.
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage: tokens survive a page reload but not a closed tab.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowRedirectInIframe: false,
    },
  }
}

let instance: PublicClientApplication | null = null

/**
 * The single PCA instance, shared across the app. Still created before any
 * rendering happens in Entra mode — the provider's boot effect is the first
 * caller, and it calls `initialize()` and `handleRedirectPromise()` before
 * reading any account state.
 */
export function getMsalInstance(): PublicClientApplication {
  instance ??= new PublicClientApplication(buildConfig())
  return instance
}

/**
 * The scope that acquires whatever App Roles the signed-in user has on the
 * API registration. Server-side authorisation uses the `roles` claim.
 */
export const API_SCOPES = [`${import.meta.env.VITE_API_AUDIENCE}/.default`]
